import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import type { AccountInfo } from '@azure/msal-browser';
import { Observable } from 'rxjs';
import type { AuthMeResponse } from '../models/auth.models';
import { AppEnvironment } from '../config/app-environment';
import { ApiClientService } from './api-client.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiClient = inject(ApiClientService);
  private readonly msalService = inject(MsalService);
  private loginRedirectInFlight = false;

  getCurrentUser(): Observable<AuthMeResponse> {
    return this.apiClient.get<AuthMeResponse>('/auth/me');
  }

  /** Starts the Entra ID login redirect. A no-op while a redirect is already
   * in flight, so e.g. several requests 401'ing at once don't each try to
   * start their own.
   *
   * Always sends the post-login redirect to / rather than letting MSAL
   * default it to the page login() was called from: when called from the
   * /login page itself, that default becomes /login, and landing back on
   * /login re-triggers LoginPageComponent's own unconditional login() call
   * -- an infinite redirect loop once a live Entra session makes each
   * round-trip near-instant. Angular's own root-path redirect then takes it
   * from / to /valuations. */
  login(): void {
    if (this.loginRedirectInFlight) {
      return;
    }

    this.loginRedirectInFlight = true;
    this.msalService
      .loginRedirect({
        scopes: [AppEnvironment.entraApiScope],
        redirectStartPage: window.location.origin,
      })
      .subscribe({
        error: (error: unknown) => {
          this.loginRedirectInFlight = false;
          console.error('MSAL login redirect failed', error);
        },
      });
  }

  logout(): void {
    this.msalService.logoutRedirect({ postLogoutRedirectUri: '/login' }).subscribe({
      error: (error: unknown) => console.error('MSAL logout redirect failed', error),
    });
  }

  /** Selects the account resolved by the post-redirect MSAL cache (if any) as
   * the active account, so subsequent MsalInterceptor token requests use it. */
  ensureActiveAccount(): AccountInfo | null {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      return null;
    }

    this.msalService.instance.setActiveAccount(account);
    return account;
  }
}
